import React from "react";
import { WZ_GLYPHS } from "../../core/constants";
import {
  ORBIT_DECO_SIZE,
  ZODIAC_RING_R,
  centerStyleForVariant,
  tracksForVariant,
} from "./orbitDecoVariants";
import { zodiacGlyphPosition } from "./zodiacRing";

const CENTERED = { position: "absolute", top: "50%", left: "50%" };

function centeredRing(r, extra = {}) {
  return {
    ...CENTERED,
    width: r * 2,
    height: r * 2,
    marginLeft: -r,
    marginTop: -r,
    borderRadius: "50%",
    ...extra,
  };
}

function orbitMotion({ r, dur, reverse }) {
  return {
    animation: `orbit ${dur}s linear infinite`,
    animationDirection: reverse ? "reverse" : "normal",
    "--orbit-radius": `${r}px`,
  };
}

function NatalZodiacRing({ acc }) {
  return (
    <div
      className="orbit-deco__zodiac-ring"
      style={centeredRing(ZODIAC_RING_R, {
        border: `1px dashed ${acc.primary}33`,
        animation: "spin-slow 60s linear infinite",
        pointerEvents: "none",
      })}
    >
      {WZ_GLYPHS.map((glyph, i) => (
        <span
          key={glyph}
          style={{
            position: "absolute",
            ...zodiacGlyphPosition(i, ZODIAC_RING_R),
            fontSize: 14,
            opacity: 0.32,
            color: acc.primary,
          }}
        >
          {glyph}
        </span>
      ))}
    </div>
  );
}

function OrbitBody({ planet, track }) {
  const { r, dur } = track;
  const { color, size, glyph, reverse } = planet;
  const motion = orbitMotion({ r, dur, reverse });

  if (glyph) {
    const box = size + 8;
    return (
      <div
        style={{
          ...CENTERED,
          width: box,
          height: box,
          marginLeft: -box / 2,
          marginTop: -box / 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size,
          color,
          textShadow: `0 0 14px ${color}cc`,
          ...motion,
        }}
      >
        {glyph}
      </div>
    );
  }

  const half = size / 2;
  return (
    <div
      style={{
        ...CENTERED,
        width: size,
        height: size,
        marginLeft: -half,
        marginTop: -half,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 14px ${color}bb`,
        ...motion,
      }}
    />
  );
}

export default function OrbitDeco({ acc, variant = "natal" }) {
  const resolved = variant === "wedding" ? "wedding" : "natal";
  const tracks = tracksForVariant(resolved, acc);
  const center = centerStyleForVariant(resolved, acc);
  const half = center.size / 2;

  return (
    <div
      className={`orbit-deco orbit-deco--${resolved}`}
      style={{ position: "relative", width: ORBIT_DECO_SIZE, height: ORBIT_DECO_SIZE, flexShrink: 0 }}
    >
      {resolved === "wedding" && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: -12,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${acc.secondary}18 0%, transparent 68%)`,
            pointerEvents: "none",
          }}
        />
      )}
      {resolved === "natal" && <NatalZodiacRing acc={acc} />}

      {tracks.map((track, i) => (
        <div
          key={`ring-${i}`}
          style={centeredRing(track.r, {
            border: track.ringBorder,
            animation: `spin-slow ${track.dur}s linear infinite`,
            animationDirection: track.ringDir,
          })}
        />
      ))}

      {tracks.map((track, i) => (
        <OrbitBody key={`planet-${i}`} planet={track.planet} track={track} />
      ))}

      {center.bands?.map((band, i) => (
        <div
          key={`band-${i}`}
          aria-hidden="true"
          style={centeredRing(band.size / 2, { border: band.border, pointerEvents: "none" })}
        />
      ))}

      <div
        aria-hidden="true"
        style={{
          ...CENTERED,
          width: center.size,
          height: center.size,
          marginLeft: -half,
          marginTop: -half,
          borderRadius: "50%",
          animation: "glow-pulse 3s ease-in-out infinite",
          background: center.background,
          boxShadow: center.boxShadow,
        }}
      />
    </div>
  );
}
